/**
 * Lightweight User-Agent Parser — No external dependencies
 * Extracts browser, OS, version, and device model from navigator.userAgent
 */

export interface ParsedUA {
  browserName: string;
  browserVersion: string;
  osName: string;
  osVersion: string;
  deviceModel: string;
}

export function parseUA(ua: string): ParsedUA {
  // ── Browser ──
  let browserName = 'Other';
  let browserVersion = '';

  if (/Edg(?:e|A|iOS)?\/(\S+)/i.test(ua)) {
    browserName = 'Edge'; browserVersion = RegExp.$1;
  } else if (/OPR\/(\S+)|Opera\/(\S+)/i.test(ua)) {
    browserName = 'Opera'; browserVersion = RegExp.$1 || RegExp.$2;
  } else if (/SamsungBrowser\/(\S+)/i.test(ua)) {
    browserName = 'Samsung Internet'; browserVersion = RegExp.$1;
  } else if (/Firefox\/(\S+)/i.test(ua)) {
    browserName = 'Firefox'; browserVersion = RegExp.$1;
  } else if (/CriOS\/(\S+)/i.test(ua)) {
    browserName = 'Chrome'; browserVersion = RegExp.$1;
  } else if (/Chrome\/(\S+)/i.test(ua) && !/Chromium/i.test(ua)) {
    browserName = 'Chrome'; browserVersion = RegExp.$1;
  } else if (/Version\/(\S+).*Safari/i.test(ua)) {
    browserName = 'Safari'; browserVersion = RegExp.$1;
  } else if (/Safari\/(\S+)/i.test(ua)) {
    browserName = 'Safari'; browserVersion = RegExp.$1;
  }

  // Clean version to major.minor
  browserVersion = browserVersion.split('.').slice(0, 2).join('.');

  // ── OS ──
  let osName = 'Other';
  let osVersion = '';

  if (/iPhone|iPad|iPod/i.test(ua)) {
    osName = 'iOS';
    const m = ua.match(/OS (\d+[_.\d]*)/);
    if (m) osVersion = m[1].replace(/_/g, '.');
  } else if (/Android (\d+[.\d]*)/i.test(ua)) {
    osName = 'Android'; osVersion = RegExp.$1;
  } else if (/Windows NT (\d+\.\d+)/i.test(ua)) {
    osName = 'Windows';
    const ntMap: Record<string, string> = { '10.0': '10/11', '6.3': '8.1', '6.2': '8', '6.1': '7' };
    osVersion = ntMap[RegExp.$1] || RegExp.$1;
  } else if (/Mac OS X (\d+[_.\d]*)/i.test(ua)) {
    osName = 'macOS';
    osVersion = RegExp.$1.replace(/_/g, '.');
  } else if (/CrOS/i.test(ua)) {
    osName = 'Chrome OS';
  } else if (/Linux/i.test(ua)) {
    osName = 'Linux';
  }

  osVersion = osVersion.split('.').slice(0, 2).join('.');

  // ── Device model ──
  let deviceModel = '';

  if (/iPad/i.test(ua)) {
    deviceModel = 'iPad';
  } else if (/iPhone/i.test(ua)) {
    deviceModel = 'iPhone';
  } else if (/iPod/i.test(ua)) {
    deviceModel = 'iPod';
  } else if (/SM-([A-Z]\d+)/i.test(ua)) {
    deviceModel = `Samsung ${RegExp.$1}`;
  } else if (/Samsung/i.test(ua)) {
    deviceModel = 'Samsung';
  } else if (/Pixel (\d+\w*)/i.test(ua)) {
    deviceModel = `Pixel ${RegExp.$1}`;
  } else if (/Huawei/i.test(ua)) {
    deviceModel = 'Huawei';
  } else if (/Xiaomi|Redmi|POCO/i.test(ua)) {
    deviceModel = 'Xiaomi';
  } else if (/OnePlus/i.test(ua)) {
    deviceModel = 'OnePlus';
  }

  return { browserName, browserVersion, osName, osVersion, deviceModel };
}
